import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { setDataUsers, setDataUsersID } from 'src/store/actions/data.actions';
import { useEffect, useMemo, useRef, useState } from 'react';

const userCache: { [key: number]: any } = {};

export const useUserData = (userID: number) => {
  const dataService = useAppSelector((state: any) => state.services).dataService;
  const dataData = useAppSelector((state: any) => state.data);
  const users = dataData.users;
  const dispatch = useAppDispatch();

  const usersRef = useRef(users);
  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  const defaultUser = users.find((u: any) => u.userID === -1);
  const placeholderUser = useMemo(() => ({ ...defaultUser, userID }), [defaultUser, userID]);

  const [user, setUser] = useState<any>(placeholderUser);

  useEffect(() => {
    let isCancelled = false;

    const foundUser = usersRef.current.find((u: any) => u.userID === userID);
    if (foundUser) {
      setUser(foundUser);
      return;
    }

    const cached = userCache[userID];
    if (cached) {
      setUser(cached);
      return;
    }

    const fetchUser = async () => {
      try {
        const res = await dataService.listUsers(userID);
        if (!isCancelled && res?.data?.users?.length > 0) {
          const newUser = res.data.users[0];
          const newUserData = {
            ...newUser,
            schoolsID: Array.isArray(newUser.schoolID) ? newUser.schoolID : [],
          };
          userCache[userID] = newUserData;
          setUser(newUserData);
          dispatch(setDataUsers([...usersRef.current, newUserData]));
          dispatch(
            setDataUsersID([
              ...new Set([
                ...usersRef.current.map((u: any) => u.userID),
                newUserData.userID,
              ]),
            ])
          );
        } else if (!isCancelled) {
          setUser(placeholderUser);
        }
      } catch {
        if (!isCancelled) setUser(placeholderUser);
      }
    };

    fetchUser();

    return () => {
      isCancelled = true;
    };
  }, [userID, dataService, dispatch, placeholderUser]);

  return user;
};